import { ConditionWrapper, Grid } from '@approvalmax/ui/src/components';
import { xeroConstants } from 'modules/common/constants';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { uploadAttachments } from 'pages/requestForm/actions';
import { AttachmentKind } from 'pages/requestForm/data/AttachmentKind';
import {
    getFieldsApprovalPermissionBySystemPurpose,
    getRequestEditMode,
} from 'pages/requestForm/selectors/requestSelectors';
import { getXeroContext } from 'pages/requestForm/selectors/xero';
import { FC, memo, useCallback } from 'react';
import { useUserBetaFeatures } from 'shared/hooks';

import { AttachmentsSection, NoteForApproversSection, RequesterInstructionSection } from '../../../components';
import { EmailToSupplierMessages } from '../../../components/EmailToSupplierForm/EmailToSupplierForm.types';
import FileDropProvider from '../../../components/FileDropProvider';
import { getContext } from '../../../selectors/pageSelectors';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import XeroEmailToSupplierSection from '../XeroEmailToSupplierSection/XeroEmailToSupplierSection';
import XeroLineItemsSection from '../XeroLineItemsSection/XeroLineItemsSection';
import XeroQuoteFieldsSection from './components/XeroQuoteFieldsSection/XeroQuoteFieldsSection';
import XeroQuoteTermsSection from './components/XeroQuoteTermsSection/XeroQuoteTermsSection';
import XeroQuoteTextInfoSection from './components/XeroQuoteTextInfoSection/XeroQuoteTextInfoSection';
import { placeholderList } from './XeroQuoteForm.constants';
import { messages } from './XeroQuoteForm.messages';
import {
    Content,
    ContentInner,
    Root,
    Toolbar,
    ToolbarHeader,
    ToolbarHeaderTextContainer,
} from './XeroQuoteForm.styles';
import { XeroQuoteFormProps } from './XeroQuoteForm.types';

const labelsEmailSection: Partial<EmailToSupplierMessages> = {
    formTitle: messages.emailToSupplierSectionName,
    toggleLabel: messages.emailToSupplierToggleLabel,
    attachmentsLabel: messages.emailToSupplierAttachmentsLabel,
    attachmentsNote: messages.emailToSupplierAttachmentsNote,
};

const XeroQuoteForm: FC<XeroQuoteFormProps> = memo((props) => {
    const { request } = props;
    const dispatch = useDispatch();
    const context = useSelector(getContext);
    const contextXero = useSelector(getXeroContext);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const [attachmentFieldPermission] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [domain.FieldSystemPurpose.XeroAttachment])
    );

    const details = request.details;
    const isEmailToVendorEnabled =
        contextXero.supplierEmailSettings?.state !== domain.TemplateSettingsEmailToSupplierState.Disabled;

    const isEmailToVendorReadonly =
        details.emailToSupplier !== null &&
        contextXero.supplierEmailSettings?.state === domain.TemplateSettingsEmailToSupplierState.Disabled;

    const onUploadAttachments = useCallback(
        (files: File[], attachmentKind: AttachmentKind) => {
            dispatch(uploadAttachments({ files, attachmentKind }));
        },
        [dispatch]
    );

    const showEmailToSupplier = isEmailToVendorEnabled || isEmailToVendorReadonly;

    const { isBelgradeUIFilesUploadSection, isBelgradeUIEmailToSupplier } = useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection && !isBelgradeUIEmailToSupplier}
            wrapperIf={(children) => (
                <FileDropProvider onUpload={onUploadAttachments} maxSize={context.attachmentMaxSize}>
                    {children}
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarHeader>
                        <ToolbarHeaderTextContainer>{messages.requestFormTitle}</ToolbarHeaderTextContainer>

                        <ActionsBlock />
                    </ToolbarHeader>

                    <XeroQuoteFieldsSection request={request} />
                </Toolbar>

                <Content>
                    <ContentInner>
                        <RequesterInstructionSection instructionText={context.requesterInstruction} />

                        <XeroQuoteTextInfoSection request={request} />

                        <ConditionWrapper
                            condition={isBelgradeUIFilesUploadSection}
                            wrapperIf={(children) => (
                                <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                                    {children}

                                    <AttachmentsSection
                                        request={request}
                                        editMode={editMode}
                                        maxFilesInList={9}
                                        extensions={xeroConstants.xeroAllowedFileExtensions}
                                        readOnly={attachmentFieldPermission.disabled}
                                        required={attachmentFieldPermission.required}
                                    />
                                </Grid>
                            )}
                        >
                            {request.origin === domain.RequestOrigin.ApprovalMax && <NoteForApproversSection />}
                        </ConditionWrapper>

                        <XeroLineItemsSection request={request} />

                        <XeroQuoteTermsSection request={request} />

                        {!isBelgradeUIFilesUploadSection && (
                            <FilesSection
                                editMode={editMode}
                                maxFilesCount={9}
                                readonly={attachmentFieldPermission.disabled}
                                required={attachmentFieldPermission.required}
                            />
                        )}

                        {showEmailToSupplier && (
                            <XeroEmailToSupplierSection
                                request={request}
                                labels={labelsEmailSection}
                                placeholderList={placeholderList}
                                isReadonly={isEmailToVendorReadonly}
                            />
                        )}
                    </ContentInner>
                </Content>
            </Root>
        </ConditionWrapper>
    );
});

export default XeroQuoteForm;
